"use client";

import Link from "next/link";
import { useState } from "react";

import {
  ArrowLeft,
  Bot,
  BookOpen,
  Check,
  ChevronDown,
  Globe,
  MessageSquare,
  Save,
  Send,
  Settings2,
  Sparkles,
  MessageCircle,
  Globe2,
  Play,
  Rocket,
} from "lucide-react";

const languages = [
  "English",
  "Filipino",
  "Ilocano",
  "Cebuano",
  "Spanish",
  "Japanese",
  "Korean",
  "Mandarin Chinese",
  "French",
  "Arabic",
];

const knowledgeBases = [
  { id: "hotel-info", name: "Hotel Information", detail: "Rooms, facilities, amenities and services" },
  { id: "booking-policy", name: "Booking Policies", detail: "Reservations, cancellations and payments" },
  { id: "faq", name: "Frequently Asked Questions", detail: "Common guest questions and answers" },
];

export default function AssistantConfiguration() {
  const [name, setName] = useState("Hotel Guest Assistant");
  const [description, setDescription] = useState(
    "Assists guests with hotel information and booking inquiries."
  );
  const [instructions, setInstructions] = useState(
    "You are a helpful and professional hotel guest assistant. Answer guest questions clearly and politely. Help guests understand hotel facilities, room options, policies and booking procedures. If you do not know an answer, do not invent information. Direct the guest to hotel staff when necessary."
  );
  const [personality, setPersonality] = useState("Professional and friendly");
  const [selectedLanguages, setSelectedLanguages] = useState(["English", "Filipino"]);
  const [selectedKnowledge, setSelectedKnowledge] = useState(["hotel-info", "booking-policy", "faq"]);
  const [websiteEnabled, setWebsiteEnabled] = useState(true);
  const [messengerEnabled, setMessengerEnabled] = useState(false);
  const [status, setStatus] = useState("Draft");
  const [notice, setNotice] = useState("");
  const [message, setMessage] = useState("");
  const [chatMessages, setChatMessages] = useState([
    {
      role: "assistant",
      text: "Hello! Welcome. I'm your hotel guest assistant. How may I help you today?",
    },
  ]);

  function toggleLanguage(language: string) {
    setSelectedLanguages((current) =>
      current.includes(language)
        ? current.filter((item) => item !== language)
        : [...current, language]
    );
  }

  function toggleKnowledge(id: string) {
    setSelectedKnowledge((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id]
    );
  }

  function sendTestMessage() {
    if (!message.trim()) return;

    const question = message.trim();

    setChatMessages((current) => [
      ...current,
      { role: "user", text: question },
      {
        role: "assistant",
        text: "This is a preview response. Once the AI service is connected, I will answer using your configured instructions and selected knowledge sources.",
      },
    ]);

    setMessage("");
  }

  function saveConfiguration() {
    setStatus("Draft");
    setNotice("Configuration validated in this browser preview. Backend saving is not yet connected.");
  }

  function publishAssistant() {
    setStatus("Published (preview)");
    setNotice("Preview publication status updated. The assistant is not yet live or connected to customers.");
  }

  return (
    <main className="min-h-screen bg-slate-50 p-6 md:p-10">
      <div className="mx-auto max-w-6xl">

        <div className="mb-8">
          <Link
            href="/assistants"
            className="mb-5 inline-flex items-center gap-2 text-sm text-slate-500 hover:text-blue-600"
          >
            <ArrowLeft size={17} />
            Back to AI Assistants
          </Link>

          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="mb-2 flex items-center gap-2 text-sm text-blue-600">
                <Bot size={18} />
                AI ASSISTANT STUDIO
              </div>

              <h1 className="text-3xl font-bold text-slate-900">
                Configure Assistant
              </h1>

              <p className="mt-2 text-slate-500">
                Customize how your assistant communicates and serves your customers.
              </p>
            </div>

            <span className={`rounded-full px-4 py-2 text-sm font-semibold ${
              status === "Published (preview)"
                ? "bg-emerald-100 text-emerald-700"
                : "bg-amber-100 text-amber-700"
            }`}>
              {status}
            </span>
          </div>
        </div>

        {notice && (
          <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
            {notice}
          </div>
        )}

        <div className="grid gap-7 lg:grid-cols-[1fr_350px]">

          <div className="space-y-7">

            {/* Assistant Identity */}
            <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="mb-6 flex items-center gap-3">
                <div className="rounded-lg bg-blue-50 p-3 text-blue-600">
                  <Bot size={22} />
                </div>
                <div>
                  <h2 className="font-bold text-slate-900">Assistant Identity</h2>
                  <p className="text-sm text-slate-500">
                    Define your assistant's name and purpose.
                  </p>
                </div>
              </div>

              <label className="mb-2 block text-sm font-semibold text-slate-700">
                Assistant Name
              </label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mb-5 w-full rounded-lg border border-slate-200 px-4 py-3 text-sm outline-none focus:border-blue-500"
                placeholder="Enter assistant name"
              />

              <label className="mb-2 block text-sm font-semibold text-slate-700">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm outline-none focus:border-blue-500"
                placeholder="Describe what this assistant does"
              />
            </section>

            {/* Personality and Instructions */}
            <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="mb-6 flex items-center gap-3">
                <div className="rounded-lg bg-violet-50 p-3 text-violet-600">
                  <Sparkles size={22} />
                </div>
                <div>
                  <h2 className="font-bold text-slate-900">
                    Personality & Instructions
                  </h2>
                  <p className="text-sm text-slate-500">
                    Define how your AI assistant should behave.
                  </p>
                </div>
              </div>

              <label className="mb-2 block text-sm font-semibold text-slate-700">
                Communication Style
              </label>
              <div className="relative mb-5">
                <select
                  value={personality}
                  onChange={(e) => setPersonality(e.target.value)}
                  className="w-full appearance-none rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-blue-500"
                >
                  <option>Professional and friendly</option>
                  <option>Formal and corporate</option>
                  <option>Warm and conversational</option>
                  <option>Casual and engaging</option>
                  <option>Concise and direct</option>
                </select>
                <ChevronDown className="pointer-events-none absolute right-4 top-3 text-slate-400" size={18} />
              </div>

              <label className="mb-2 block text-sm font-semibold text-slate-700">
                System Instructions
              </label>
              <textarea
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                rows={7}
                className="w-full rounded-lg border border-slate-200 px-4 py-3 text-sm leading-6 outline-none focus:border-blue-500"
                placeholder="Tell your assistant how to behave, what to do, and what to avoid."
              />
              <p className="mt-2 text-xs text-slate-400">
                These instructions will guide the assistant's responses when connected to the AI service.
              </p>
            </section>

            {/* Languages */}
            <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="mb-5 flex items-center gap-3">
                <div className="rounded-lg bg-emerald-50 p-3 text-emerald-600">
                  <Globe size={22} />
                </div>
                <div>
                  <h2 className="font-bold text-slate-900">
                    Supported Languages
                  </h2>
                  <p className="text-sm text-slate-500">
                    Choose the languages your assistant should support.
                  </p>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                {languages.map((language) => (
                  <label
                    key={language}
                    className="flex cursor-pointer items-center gap-3 rounded-lg border border-slate-200 p-3 text-sm text-slate-700 hover:bg-slate-50"
                  >
                    <input
                      type="checkbox"
                      checked={selectedLanguages.includes(language)}
                      onChange={() => toggleLanguage(language)}
                      className="h-4 w-4 accent-blue-600"
                    />
                    {language}
                  </label>
                ))}
              </div>

              <p className="mt-4 text-xs text-slate-400">
                Selected: {selectedLanguages.length} language(s)
              </p>
            </section>

            {/* Knowledge Base */}
            <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="mb-5 flex items-center gap-3">
                <div className="rounded-lg bg-amber-50 p-3 text-amber-600">
                  <BookOpen size={22} />
                </div>
                <div>
                  <h2 className="font-bold text-slate-900">Knowledge Base</h2>
                  <p className="text-sm text-slate-500">
                    Select information sources for this assistant.
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                {knowledgeBases.map((kb) => (
                  <label
                    key={kb.id}
                    className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 p-4 hover:bg-slate-50"
                  >
                    <input
                      type="checkbox"
                      checked={selectedKnowledge.includes(kb.id)}
                      onChange={() => toggleKnowledge(kb.id)}
                      className="mt-1 h-4 w-4 accent-blue-600"
                    />
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{kb.name}</p>
                      <p className="mt-1 text-xs text-slate-500">{kb.detail}</p>
                    </div>
                  </label>
                ))}
              </div>

              <p className="mt-4 text-xs text-slate-400">
                These are sample knowledge sources. Actual documents and retrieval will be connected later.
              </p>
            </section>

            {/* Channels */}
            <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="mb-5 flex items-center gap-3">
                <div className="rounded-lg bg-sky-50 p-3 text-sky-600">
                  <Globe2 size={22} />
                </div>
                <div>
                  <h2 className="font-bold text-slate-900">Channels</h2>
                  <p className="text-sm text-slate-500">
                    Choose where your assistant will be made available.
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between gap-4 rounded-lg border border-slate-200 p-4">
                  <div className="flex items-center gap-3">
                    <Globe size={22} className="text-blue-600" />
                    <div>
                      <p className="text-sm font-semibold text-slate-800">Website Chatbot</p>
                      <p className="text-xs text-slate-500">Embed the assistant on your website.</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setWebsiteEnabled(!websiteEnabled)}
                    className={`rounded-full px-4 py-2 text-xs font-semibold ${
                      websiteEnabled ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {websiteEnabled ? "Enabled" : "Disabled"}
                  </button>
                </div>

                <div className="flex items-center justify-between gap-4 rounded-lg border border-slate-200 p-4">
                  <div className="flex items-center gap-3">
                    <MessageCircle size={22} className="text-blue-700" />
                    <div>
                      <p className="text-sm font-semibold text-slate-800">Facebook Messenger</p>
                      <p className="text-xs text-slate-500">Connect your business Facebook Page.</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setMessengerEnabled(!messengerEnabled)}
                    className={`rounded-full px-4 py-2 text-xs font-semibold ${
                      messengerEnabled ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {messengerEnabled ? "Enabled" : "Disabled"}
                  </button>
                </div>
              </div>

              <p className="mt-4 text-xs text-slate-400">
                These are configuration previews only. No live channel connection has been established.
              </p>
            </section>

            {/* Save and Publish */}
            <section className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <div>
                <p className="font-semibold text-slate-800">Assistant Configuration</p>
                <p className="mt-1 text-xs text-slate-500">
                  Save your changes or preview the publication state.
                </p>
              </div>

              <div className="flex flex-wrap gap-3">
                <button
                  onClick={saveConfiguration}
                  className="flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  <Save size={17} />
                  Save Draft
                </button>

                <button
                  onClick={publishAssistant}
                  className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700"
                >
                  <Rocket size={17} />
                  Publish
                </button>
              </div>
            </section>

          </div>

          {/* Test Playground */}
          <aside className="h-fit overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm lg:sticky lg:top-6">
            <div className="border-b border-slate-200 p-5">
              <div className="flex items-center gap-2">
                <MessageSquare size={20} className="text-blue-600" />
                <h2 className="font-bold text-slate-900">Test Playground</h2>
              </div>
              <p className="mt-2 text-xs text-slate-500">
                Preview a conversation with your assistant.
              </p>
            </div>

            <div className="min-h-80 space-y-4 bg-slate-50 p-4">
              {chatMessages.map((chat, index) => (
                <div
                  key={index}
                  className={`flex ${chat.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div className={`max-w-[90%] rounded-xl px-4 py-3 text-sm leading-6 ${
                    chat.role === "user"
                      ? "bg-blue-600 text-white"
                      : "border border-slate-200 bg-white text-slate-700"
                  }`}>
                    {chat.text}
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-slate-200 p-4">
              <div className="flex gap-2">
                <input
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") sendTestMessage();
                  }}
                  placeholder="Ask your assistant..."
                  className="min-w-0 flex-1 rounded-lg border border-slate-200 px-3 py-3 text-sm outline-none focus:border-blue-500"
                />
                <button
                  onClick={sendTestMessage}
                  className="rounded-lg bg-blue-600 p-3 text-white hover:bg-blue-700"
                  aria-label="Send test message"
                >
                  <Send size={18} />
                </button>
              </div>

              <p className="mt-3 text-xs text-slate-400">
                <Play size={12} className="mr-1 inline" />
                Simulated preview. AI model connection is not active yet.
              </p>
            </div>
          </aside>

        </div>
      </div>
    </main>
  );
}
